<template>
  <el-card shadow="never" v-loading="loading">
    <el-descriptions title="Invoicing" :colon="false" :column="1" size="medium">
      <template slot="extra">
        <el-button
          size="small"
          type="primary"
          icon="el-icon-refresh"
          circle
          @click="getMonthlyInvoices"
        >
        </el-button>
      </template>
    </el-descriptions>
    <el-alert v-if="error" :title="error" type="error" show-icon></el-alert>
    <el-table
      :data="monthlyInvoices"
      empty-text="No monthly invoices found"
      :default-sort="{ prop: 'generatedAt', order: 'descending' }"
    >
      <el-table-column type="expand">
        <template slot-scope="scope">
          <el-table
            :data="scope.row.lineItemsSnapshot || []"
            size="mini"
            border
          >
            <el-table-column label="Period" width="120">
              <template slot-scope="lineItemScope">
                {{ lineItemScope.row.period | formatDate }}
              </template>
            </el-table-column>
            <el-table-column label="Unit" width="120">
              <template slot-scope="lineItemScope">
                {{ getUnitName(lineItemScope.row) }}
              </template>
            </el-table-column>
            <el-table-column label="Type" width="130">
              <template slot-scope="lineItemScope">
                <el-tag size="mini">
                  {{ lineItemScope.row.details || lineItemScope.row.chargeType || '-' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Description" prop="description"></el-table-column>
            <el-table-column label="Amount" width="140" align="right">
              <template slot-scope="lineItemScope">
                {{ lineItemScope.row.amount | currencySign(getFacilityData(scope.row)) }}
              </template>
            </el-table-column>
          </el-table>
        </template>
      </el-table-column>
      <el-table-column sortable label="Period" prop="periodKey" width="120">
        <template slot-scope="scope">
          <el-tag>{{ getPeriodLabel(scope.row) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        sortable
        label="Invoice #"
        prop="invoiceNumber"
        min-width="210"
      >
      </el-table-column>
      <el-table-column sortable label="Generated At" prop="generatedAt" width="160">
        <template slot-scope="scope">
          {{ scope.row.generatedAt | formatDate }}
        </template>
      </el-table-column>
      <el-table-column label="Status" width="120">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 'SUPERSEDED' ? 'warning' : 'success'">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Items" width="90" align="center">
        <template slot-scope="scope">
          {{ getLineItemCount(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column label="Total" width="140" align="right">
        <template slot-scope="scope">
          <strong>
            {{ getInvoiceTotal(scope.row) | currencySign(getFacilityData(scope.row)) }}
          </strong>
        </template>
      </el-table-column>
      <el-table-column label="File" fixed="right" width="130" align="center">
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.url"
            size="mini"
            type="primary"
            icon="el-icon-view"
            @click="() => openPreview(scope.row.url)"
          >
          </el-button>
          <a
            v-if="scope.row.url"
            :href="scope.row.url"
            target="_blank"
            rel="noopener"
            download
          >
            <el-button
              size="mini"
              type="primary"
              icon="el-icon-download"
            >
            </el-button>
          </a>
          <span v-if="!scope.row.url">-</span>
        </template>
      </el-table-column>
    </el-table>
    <el-dialog
      title="Monthly Invoice"
      :visible.sync="dialogForPdf"
      destroy-on-close
      width="75%"
      @close="closePreview"
    >
      <PreviewPdf v-if="selectedDocument" :source="selectedDocument" />
    </el-dialog>
  </el-card>
</template>

<script>
import getCustomerMonthlyInvoices from '../api/monthlyInvoices';
import PreviewPdf from './PreviewPdf';

export default {
  name: 'CustomerBilling',
  components: { PreviewPdf },
  props: {
    id: { type: String, required: true },
    facility: { type: String, default: null },
  },
  data() {
    return {
      monthlyInvoices: [],
      selectedDocument: null,
      dialogForPdf: false,
      loading: false,
      error: '',
    };
  },
  watch: {
    async facility() {
      await this.getMonthlyInvoices();
    },
    async id() {
      await this.getMonthlyInvoices();
    },
  },
  async mounted() {
    await this.getMonthlyInvoices();
  },
  methods: {
    async getMonthlyInvoices() {
      this.loading = true;
      this.error = '';

      try {
        const response = await getCustomerMonthlyInvoices(
          this.id,
          this.facility,
          this.$keycloak.token,
        );
        this.monthlyInvoices = response.data;
      } catch (err) {
        this.error = 'Unable to load monthly invoices.';
        this.monthlyInvoices = [];
      }

      this.loading = false;
    },
    getFacilityData(invoice) {
      return invoice.facility || this.$root.state.facilityData;
    },
    getInvoiceTotal(invoice) {
      if (!invoice || !invoice.totals || !Number(invoice.totals.total)) {
        return 0;
      }

      return invoice.totals.total;
    },
    getLineItemCount(invoice) {
      return invoice && invoice.lineItemsSnapshot ? invoice.lineItemsSnapshot.length : 0;
    },
    getPeriodLabel(invoice) {
      if (invoice.periodKey) {
        return invoice.periodKey;
      }

      if (!invoice.periodStart) {
        return '-';
      }

      return new Date(invoice.periodStart).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    },
    getUnitName(lineItem) {
      if (!lineItem || !lineItem.unit) {
        return '-';
      }

      if (typeof lineItem.unit === 'object') {
        return lineItem.unit.name || '-';
      }

      return '-';
    },
    openPreview(url) {
      if (!url) {
        return;
      }

      this.selectedDocument = url;
      this.dialogForPdf = true;
    },
    closePreview() {
      this.selectedDocument = null;
      this.dialogForPdf = false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/CustomerBilling.vue